import Navigation from '../components/Navigation'

export default function TermsOfUsePage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <Navigation />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-8">Terms of Use</h1>

        <div className="prose prose-lg dark:prose-invert max-w-none">
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            <strong>Last Updated:</strong> October 22, 2025
          </p>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              By accessing or using ContentIndexer ("Service"), you agree to be bound by these Terms of Use ("Terms").
              If you do not agree to these Terms, please do not use the Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">2. Description of Service</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              ContentIndexer provides AI-powered content analysis and indexing services for videos, audio, and text files.
              The Service operates on a prepaid credit system where users purchase credits to process content through
              various extractors and analysis tools.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">3. Account Registration and Security</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              You may register for an account using OAuth providers (GitHub, Google). You are responsible for:
            </p>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li>Maintaining the security of your account credentials</li>
              <li>All activities that occur under your account</li>
              <li>Notifying us immediately of any unauthorized use</li>
              <li>Ensuring your account information is accurate and current</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">4. Pricing and Payment</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              <strong>Prepaid Credits:</strong> The Service operates on a prepaid credit model. You purchase credits
              in advance which are used to pay for content processing.
            </p>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li>Credits never expire</li>
              <li>Credits are non-refundable once purchased</li>
              <li>Larger credit packages include bonus credits (up to 25%)</li>
              <li>Prices are subject to change with 30 days' notice</li>
              <li>All payments are processed securely through Stripe</li>
            </ul>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              <strong>Free Monthly Credits:</strong> Eligible accounts receive $5 in free credits monthly.
              Free credits may be subject to usage limits and expire at the end of each month.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">5. Acceptable Use Policy</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              You agree not to use the Service to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li>Process content you do not own or have permission to analyze</li>
              <li>Upload illegal, harmful, or infringing content</li>
              <li>Violate any applicable laws or regulations</li>
              <li>Interfere with or disrupt the Service or servers</li>
              <li>Attempt to gain unauthorized access to any part of the Service</li>
              <li>Use the Service for any malicious or fraudulent purposes</li>
              <li>Resell or redistribute the Service without authorization</li>
              <li>Reverse engineer or attempt to extract source code</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">6. Content and Intellectual Property</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              <strong>Your Content:</strong> You retain all ownership rights to content you upload. By using the Service,
              you grant us a limited license to process, analyze, and store your content as necessary to provide the Service.
            </p>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              <strong>Our Service:</strong> ContentIndexer and all related technology, software, and documentation are
              protected by intellectual property laws. You may not copy, modify, or distribute our Service without permission.
            </p>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              <strong>Content Deletion:</strong> You may delete your content at any time. We will delete your content
              from our systems within 30 days of deletion request.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">7. API Usage</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              If you use our API:
            </p>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li>You must use valid API keys for authentication</li>
              <li>Rate limits may apply to prevent abuse</li>
              <li>You are responsible for securing your API keys</li>
              <li>We may monitor API usage for security and performance</li>
              <li>We reserve the right to revoke API access for violations</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">8. Service Availability and Limitations</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              We strive to provide reliable service but cannot guarantee:
            </p>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li>Uninterrupted or error-free operation</li>
              <li>100% accuracy of analysis results</li>
              <li>Availability during maintenance periods</li>
              <li>Compatibility with all content formats</li>
            </ul>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              We reserve the right to modify, suspend, or discontinue any part of the Service with notice when possible.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">9. Limitation of Liability</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW:
            </p>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li>The Service is provided "AS IS" without warranties of any kind</li>
              <li>We are not liable for any indirect, incidental, or consequential damages</li>
              <li>Our total liability shall not exceed the amount you paid in the past 12 months</li>
              <li>We are not responsible for third-party services or content</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">10. Indemnification</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              You agree to indemnify and hold harmless ContentIndexer from any claims, damages, or expenses arising from:
            </p>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li>Your use of the Service</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any third-party rights</li>
              <li>Content you upload or process</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">11. Termination</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              We may terminate or suspend your account:
            </p>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li>For violation of these Terms</li>
              <li>For fraudulent or illegal activity</li>
              <li>For extended periods of inactivity</li>
              <li>At our discretion with notice when possible</li>
            </ul>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              You may terminate your account at any time by contacting support. Unused credits are non-refundable upon termination.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">12. Changes to Terms</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              We may update these Terms from time to time. We will notify you of significant changes via email or
              through the Service. Continued use of the Service after changes constitutes acceptance of the new Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">13. Governing Law and Disputes</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              These Terms are governed by the laws of the jurisdiction where ContentIndexer operates.
              Any disputes shall be resolved through binding arbitration in accordance with applicable arbitration rules.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">14. Contact Information</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              For questions about these Terms, please contact us through our support channels.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">15. Severability</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              If any provision of these Terms is found to be unenforceable, the remaining provisions will remain in full effect.
            </p>
          </section>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex justify-center gap-6 mb-4">
              <a href="/terms" className="text-gray-400 hover:text-white transition-colors">
                Terms of Use
              </a>
              <a href="/privacy" className="text-gray-400 hover:text-white transition-colors">
                Privacy Policy
              </a>
            </div>
            <p className="text-gray-400">&copy; 2025 ContentIndexer. Built with Cloudflare Workers & React.</p>
          </div>
        </div>
      </footer>
    </div>
  )
}
